import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Send, Bot, XCircle, StickyNote, Clock, Paperclip } from "lucide-react";
import { api, API, errMsg } from "../lib/api";
import { Empty, PlainSelect } from "./Common";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { useWaTemplates } from "./WaManager";

const FILTERS = [["", "All"], ["waiting", "Needs a person"], ["operator", "With team"], ["ai", "AI"], ["closed", "Closed"]];
const BADGE = { waiting: "bg-amber-500/15 text-amber-800", operator: "bg-primary/10 text-primary", ai: "bg-muted text-muted-foreground", closed: "bg-muted text-muted-foreground" };
const BUBBLE = { visitor: "bg-muted", operator: "bg-primary text-primary-foreground", ai: "bg-secondary", note: "bg-amber-100 text-amber-900 border border-amber-300" };
const time = (iso) => new Date(iso).toLocaleString([], { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

function Bubble({ m, sid }) {
  if (m.role === "system") return <p className="text-center text-xs text-muted-foreground">{m.text}</p>;
  const media = m.media && `${API}/wa/chats/${sid}/media/${m.id}`;
  return (
    <div className={`flex ${m.role === "visitor" ? "justify-start" : "justify-end"}`}>
      <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap ${BUBBLE[m.role] || "bg-muted"}`}>
        <div className="text-[10px] font-semibold opacity-60">{m.role === "note" ? `Internal note · ${m.author}` : m.role === "ai" ? `AI · ${m.author}` : m.author || "Client"} · {time(m.at)}{m.status && m.role !== "visitor" ? ` · ${m.status}` : ""}</div>
        {media && m.media.type === "image" ? <a href={media} target="_blank" rel="noreferrer"><img src={media} alt="" className="rounded-lg max-h-56 my-1" /></a>
          : media && <a href={media} target="_blank" rel="noreferrer" className="underline flex items-center gap-1"><Paperclip className="h-3 w-3" />Open {m.media.type}</a>}
        {m.text}{m.error && <div className="text-[11px] text-destructive mt-1">Not delivered: {m.error}</div>}
      </div>
    </div>
  );
}

function TemplateSender({ sid, onSent }) {
  const { items } = useWaTemplates();
  const [sel, setSel] = useState("");
  const [params, setParams] = useState([]);
  const approved = (items || []).filter((t) => t.status === "APPROVED");
  const t = approved.find((x) => `${x.name}|${x.language}` === sel);
  const send = () => api.post(`/wa/chats/${sid}/template`, { name: t.name, language: t.language, params: params.slice(0, t.vars) })
    .then(() => { toast.success("Template sent"); onSent(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="border-t p-3 space-y-2" data-testid="wa-template-sender">
      <p className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="h-3.5 w-3.5" />More than 24h since their last message: WhatsApp only allows an approved template until they reply.</p>
      <div className="flex flex-wrap gap-2">
        <PlainSelect data-testid="wa-template-select" value={sel} onChange={(e) => { setSel(e.target.value); setParams([]); }}>
          <option value="">Choose an approved template…</option>{approved.map((x) => <option key={`${x.name}|${x.language}`} value={`${x.name}|${x.language}`}>{x.name} ({x.language})</option>)}</PlainSelect>
        {t && Array.from({ length: t.vars }).map((_, i) => <Input key={i} className="max-w-[160px]" data-testid={`wa-template-param-${i + 1}`} placeholder={`{{${i + 1}}}`} value={params[i] || ""} onChange={(e) => { const p = [...params]; p[i] = e.target.value; setParams(p); }} />)}
        <Button className="rounded-full" data-testid="wa-template-send" disabled={!t} onClick={send}><Send className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}

function Composer({ c, act }) {
  const [text, setText] = useState("");
  const [note, setNote] = useState(false);
  const submit = (e) => {
    e.preventDefault();
    if (text.trim()) act(api.post(`/wa/chats/${c.id}/${note ? "note" : "reply"}`, { text })).then(() => setText(""));
  };
  return (
    <form className="flex gap-2 border-t p-3" onSubmit={submit}>
      <Button type="button" size="icon" variant={note ? "default" : "outline"} title="Internal note" data-testid="wa-note-toggle" onClick={() => setNote(!note)}><StickyNote className="h-4 w-4" /></Button>
      <Input data-testid="wa-reply-input" placeholder={note ? "Internal note - only the team sees this" : "Reply on WhatsApp (the AI will pause)…"} value={text} onChange={(e) => setText(e.target.value)} />
      <Button className="rounded-full" data-testid="wa-reply-send" disabled={!text.trim()}><Send className="h-4 w-4" /></Button>
    </form>
  );
}

function WaThread({ id, onChanged }) {
  const [c, setC] = useState(null);
  const load = useCallback(() => api.get(`/wa/chats/${id}`).then((r) => setC(r.data)), [id]);
  useEffect(() => { load(); const h = setInterval(load, 5000); return () => clearInterval(h); }, [load]);
  const act = (p) => p.then(() => { load(); onChanged(); }).catch((e) => { toast.error(errMsg(e)); throw e; });
  if (!c) return null;
  const name = c.client ? `${c.client.first_name} ${c.client.last_name || ""}`.trim() : c.name;
  return (
    <div className="flex flex-col h-full" data-testid="wa-thread">
      <div className="flex flex-wrap items-center gap-2 border-b p-4">
        <div className="flex-1 min-w-[160px]"><div className="font-semibold">{c.client ? <Link to={`/app/clients/${c.client.id}`} className="hover:underline" data-testid="wa-thread-client-link">{name}</Link> : name}</div>
          <div className="text-xs text-muted-foreground">+{c.phone} · {c.client?.marketing_consent ? "receives offers" : "no offers"} · {c.window_open ? "reply window open" : "window closed"}</div></div>
        {c.status !== "ai" && <Button size="sm" variant="outline" className="rounded-full" data-testid="wa-back-to-ai" onClick={() => act(api.post(`/wa/chats/${id}/status`, { status: "ai" })).catch(() => {})}><Bot className="h-4 w-4 mr-1" />Let AI reply</Button>}
        {c.status !== "closed" && <Button size="sm" variant="ghost" data-testid="wa-close" onClick={() => act(api.post(`/wa/chats/${id}/status`, { status: "closed" })).catch(() => {})}><XCircle className="h-4 w-4 mr-1" />Close</Button>}
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-2">{c.messages.map((m) => <Bubble key={m.id} m={m} sid={id} />)}</div>
      {c.window_open ? <Composer c={c} act={(p) => act(p)} /> : <TemplateSender sid={id} onSent={load} />}
    </div>
  );
}

export function WaInbox() {
  const [status, setStatus] = useState("");
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState(null);
  const load = useCallback(() => api.get("/wa/chats", { params: { status } }).then((r) => setItems(r.data)).catch(() => setItems([])), [status]);
  useEffect(() => { load(); const h = setInterval(load, 6000); return () => clearInterval(h); }, [load]);
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4">{FILTERS.map(([k, l]) => <Button key={k || "all"} size="sm" variant={status === k ? "default" : "outline"} className="rounded-full" data-testid={`wa-filter-${k || "all"}`} onClick={() => setStatus(k)}>{l}</Button>)}</div>
      <div className="grid lg:grid-cols-[340px_1fr] gap-4">
        <div className="space-y-2" data-testid="wa-list">
          {!items.length && <Empty text="No WhatsApp conversations yet. Messages to your connected number appear here." />}
          {items.map((c) => (
            <button key={c.id} data-testid={`wa-chat-${c.id}`} onClick={() => setSel(c.id)} className={`w-full text-left rounded-[var(--radius)] border bg-card p-3 hover:bg-muted/40 ${sel === c.id ? "ring-2 ring-primary" : ""}`}>
              <div className="flex items-center gap-2"><span className="font-medium text-sm flex-1 truncate">{c.name || `+${c.phone}`}</span>
                {c.unread > 0 && <span className="h-2 w-2 rounded-full bg-primary" />}
                <span className={`text-[10px] rounded-full px-2 py-0.5 ${BADGE[c.status]}`}>{c.status}</span></div>
              <div className="text-xs text-muted-foreground truncate mt-1">{c.last}</div>
              <div className="text-[10px] text-muted-foreground mt-1">{time(c.updated_at)}</div>
            </button>))}
        </div>
        <div className="rounded-[var(--radius)] border bg-card min-h-[520px] h-[70vh]">{sel ? <WaThread key={sel} id={sel} onChanged={load} /> : <div className="h-full grid place-items-center text-sm text-muted-foreground">Select a conversation</div>}</div>
      </div>
    </div>
  );
}
